import { useMutation } from "@apollo/client";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/outline";
import discussedTopicSvg from "@static/img/topic-discussed.svg";
import undiscussedTopicSvg from "@static/img/topic-undiscussed.svg";
import { Menu, MenuDivider, MenuItem } from "@szhsin/react-menu";
import { compact, toInteger, uniqBy } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import store from "store2";
import {
  AddPreviousMeetingTopicsToCurrentMeetingMutationMutation,
  AddPreviousMeetingTopicsToCurrentMeetingMutationMutationVariables,
  MarkPreviousMeetingTopicsAsDiscussedMutationMutation,
  MarkPreviousMeetingTopicsAsDiscussedMutationMutationVariables,
  MeetingViewMeetingNodeNewPageFragmentFragment,
  ToggleTopicStateMutationMutation,
  ToggleTopicStateMutationMutationVariables,
  TopicState,
} from "types/graphql-schema";

import addPreviousMeetingTopicsMutation from "@apps/meeting-new/graphql/add-previous-meeting-topics-mutation";
import markPreviousMeetingTopicsDiscussedMutation from "@apps/meeting-new/graphql/mark-previous-meeting-topics-discussed-mutation";
import toggleTopicStateMutation from "@apps/topic-cta/graphql/toggle-topic-state-mutation";
import useLabel from "@apps/use-label/use-label";
import { successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { getAssetUrl } from "@helpers/helpers";
import { pluralize } from "@helpers/string";

const storageKey = "meeting-dismissed-undiscussed-topics";
const howManyDaysAreTheMeetingNotesPersisted = 7;

const UndiscussedTopicsBanner = ({
  meeting,
  onTopicsChanged,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  onTopicsChanged?: () => void;
}) => {
  const label = useLabel();
  const previousMeeting = meeting.previousMeeting;
  const dismissedMeetings: { id: number; time: number }[] =
    store(storageKey) || [];

  const isPreviousMeetingDismissed =
    previousMeeting &&
    dismissedMeetings.some(
      (dismissedMeeting) => dismissedMeeting.id === previousMeeting.id,
    );
  const [dismissed, setDismissed] = useState(isPreviousMeetingDismissed);
  const [expanded, setExpanded] = useState(false);
  const [discussedTopicIds, setDiscussedTopicIds] = useState<Set<number>>(
    new Set(),
  );

  useEffect(() => {
    setDismissed(Boolean(isPreviousMeetingDismissed));
  }, [isPreviousMeetingDismissed, previousMeeting?.id]);

  useEffect(() => {
    setDiscussedTopicIds((prev) => {
      if (prev.size === 0) return prev;
      const currentUndiscussedIds = new Set(
        compact(meeting.undiscussedPreviousMeetingTopics ?? []).map(
          (t) => t.id,
        ),
      );
      const next = new Set(
        [...prev].filter((id) => !currentUndiscussedIds.has(id)),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [meeting.undiscussedPreviousMeetingTopics]);

  const handleDismiss = () => {
    if (!previousMeeting) return;

    // remove old dismissed meetings to keep the storage somewhat cleaned.
    const pastDateThreshold =
      Date.now() - howManyDaysAreTheMeetingNotesPersisted * 24 * 60 * 60 * 1000;
    const updateDismissedMeetings = uniqBy(
      [
        ...dismissedMeetings,
        { id: previousMeeting.id, time: toInteger(Date.now() / 1000) },
      ],
      "id",
    ).filter(
      (dismissedMeeting) => dismissedMeeting.time * 1000 > pastDateThreshold,
    );
    store(storageKey, updateDismissedMeetings);
    setDismissed(true);
  };

  const [addTopics, { loading: addLoading }] = useMutation<
    AddPreviousMeetingTopicsToCurrentMeetingMutationMutation,
    AddPreviousMeetingTopicsToCurrentMeetingMutationMutationVariables
  >(addPreviousMeetingTopicsMutation);

  const [markDiscussed, { loading: markLoading }] = useMutation<
    MarkPreviousMeetingTopicsAsDiscussedMutationMutation,
    MarkPreviousMeetingTopicsAsDiscussedMutationMutationVariables
  >(markPreviousMeetingTopicsDiscussedMutation);

  const [toggleTopicState] = useMutation<
    ToggleTopicStateMutationMutation,
    ToggleTopicStateMutationMutationVariables
  >(toggleTopicStateMutation);

  const undiscussedTopics = compact(
    meeting.undiscussedPreviousMeetingTopics ?? [],
  );

  const previousMeetingIsCompleted = moment().isAfter(
    moment(meeting.previousMeeting?.startDatetime),
    "minute",
  );

  const meetingIsTooFarInThePast = moment(meeting.startDatetime).isBefore(
    moment().subtract(howManyDaysAreTheMeetingNotesPersisted, "day"),
    "day",
  );
  if (
    meetingIsTooFarInThePast ||
    !previousMeeting ||
    dismissed ||
    undiscussedTopics.length === 0 ||
    !previousMeetingIsCompleted
  ) {
    return null;
  }

  const topicCount = undiscussedTopics.length;
  const isLoading = addLoading || markLoading;

  const handleAddAll = (position: "top" | "bottom") => {
    addTopics({
      variables: { meetingId: meeting.id, position },
      onCompleted: () => {
        successNotificationVar({
          title: "Topics from the last meeting were added.",
          timeout: 3000,
        });
        handleDismiss();
        onTopicsChanged?.();
      },
    });
  };

  const handleMarkAllDiscussed = () => {
    markDiscussed({
      variables: { meetingId: meeting.id },
      onCompleted: () => {
        successNotificationVar({
          title: "Topics from the last meeting were marked as discussed.",
          timeout: 3000,
        });
        handleDismiss();
      },
    });
  };

  const handleMarkTopicDiscussed = (topicId: number) => {
    setDiscussedTopicIds((prev) => new Set(prev).add(topicId));
    toggleTopicState({
      variables: { topicId, state: TopicState.ClosedToDiscussion },
      refetchQueries: ["getMeetingNewPageQuery"],
      onCompleted: () => {
        successNotificationVar({
          title: "Topic marked as discussed.",
          timeout: 3000,
        });
      },
    });
  };

  return (
    <div className="flex flex-col bg-white border border-gray-200 rounded-md px-3 pt-4 pb-4">
      <div
        className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-50 rounded-[6px] transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="tf-header-base-dark">
          {topicCount} topic{topicCount !== 1 ? "s" : ""}{" "}
          {pluralize("was", topicCount)}n't discussed last meeting
        </span>
        <Button
          theme={buttonTheme.iconGray}
          size={ButtonSize.medium}
          square
          icon={expanded ? ChevronUpIcon : ChevronDownIcon}
          aria-label={expanded ? "Collapse" : "Expand"}
        />
      </div>
      {expanded && (
        <div className="flex flex-col gap-5 mt-2">
          <div className="flex flex-col gap-1 px-2">
            {undiscussedTopics.map(
              (topic) =>
                topic && (
                  <div key={topic.id} className="flex gap-2 items-center">
                    <button
                      onClick={() => handleMarkTopicDiscussed(topic.id)}
                      disabled={isLoading || discussedTopicIds.has(topic.id)}
                      className="flex items-center justify-center h-6 w-6 shrink-0 rounded hover:bg-gray-100 disabled:opacity-50"
                      aria-label={`Mark "${topic.title}" as discussed`}
                    >
                      <img
                        src={getAssetUrl(
                          discussedTopicIds.has(topic.id)
                            ? discussedTopicSvg
                            : undiscussedTopicSvg,
                        )}
                        className="h-4 w-4"
                      />
                    </button>
                    <span
                      className={`text-sm transition-all duration-300 ${
                        discussedTopicIds.has(topic.id)
                          ? "text-gray-400 line-through"
                          : "text-gray-800"
                      }`}
                    >
                      {topic.title}
                    </span>
                  </div>
                ),
            )}
          </div>
          <div className="flex gap-2 items-center flex-wrap px-3 pb-2">
            <Menu
              portal
              className="z-dropdown"
              align="start"
              transition={false}
              gap={6}
              aria-label="Undiscussed topics actions menu"
              menuButton={
                <Button
                  theme={buttonTheme.primary}
                  disabled={isLoading}
                  size={ButtonSize.medium}
                >
                  Actions
                  <ChevronDownIcon className="h-3.5 w-3.5 ml-1" />
                </Button>
              }
            >
              <MenuItem className="text-sm" onClick={() => handleAddAll("top")}>
                <TopicflowIcon
                  type="alignTop"
                  className="mr-3 h-5 w-5 text-gray-400"
                />
                Add all to top of this meeting
              </MenuItem>
              <MenuItem
                className="text-sm"
                onClick={() => handleAddAll("bottom")}
              >
                <TopicflowIcon
                  type="alignBottom"
                  className="mr-3 h-5 w-5 text-gray-400"
                />
                Add all to bottom of this meeting
              </MenuItem>
              <MenuDivider />
              <MenuItem className="text-sm" onClick={handleMarkAllDiscussed}>
                <TopicflowIcon
                  type="check"
                  className="mr-3 h-5 w-5 text-gray-400"
                />
                Mark all as discussed
              </MenuItem>
            </Menu>
            {meeting.previousMeeting && (
              <Button
                theme={buttonTheme.default}
                to={`/meeting/${meeting.meetingGroupId}/${meeting.previousMeeting.id}`}
                size={ButtonSize.medium}
              >
                View last{" "}
                {meeting.meetingGroup?.isFormalOneonone
                  ? label("1-on-1")
                  : "meeting"}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UndiscussedTopicsBanner;
